import { __decorate } from "tslib";
import { html } from 'lit';
import { CipublicElement } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/mixins/base.js';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit-html/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { emit, modalToggleEvent } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/event.js';
import { submitForm } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/formdata.js';
import { ContextConsumer } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/context/controllers/context-consumer.js';
import { dynamicLinksContext } from '@cigna/cigna_csg_gsg_campaigns/lib/interfaces.js';
import { updateDynamicLinks } from '@cigna/cigna_csg_gsg_cipublic-link/lib/service.js';
import { configContext } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/config.js';
import '@cigna/cigna_csg_gsg_cipublic-icon/cipublic-icon.js';
import styles from './button.scss.js';
import variants from './variants.scss.js';
/**
 * cipublic-button
 *
 * @slot content
 * @slot
 *
 * @csspart button - the button element
 */
export class Button extends CipublicElement {
    constructor() {
        super();
        /**
         * button disabled state
         */
        this.disabled = false;
        /**
         * Indicates if the button only has an icon (no text)
         */
        this.iconOnly = false;
        /**
         *  Checker to make it a Modal button
         */
        this.isModalToggle = false;
        /**
         * Constructor name.
         */
        this._name = 'button';
        /**
         * The button size (default or small).
         */
        this.size = 'default';
        /**
         * The default behavior if the button is rendered as `<button>`.
         */
        this.type = 'button'; // menu
        /**
         * Button variant.
         */
        this.variant = 'primary';
        this.emitConnectedCallback = false;
        new ContextConsumer(this, dynamicLinksContext, (value) => (this.campaignsObj = value), true);
        // if we're passing in more than the dental link, we'll want to revist how we're passing
        // into the link campaign service
        new ContextConsumer(this, configContext, (config) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            this.endpoint = config.endpoints.dentalCampaign;
        });
    }
    static get styles() {
        return [styles, variants];
    }
    set rel(val) {
        const oldVal = this._rel;
        this._rel = val;
        this.requestUpdate('rel', oldVal);
    }
    /**
     * The relationship of the linked URL as space-separated link types. `noopener` if target is `_blank` and `noreferrer`/`noopener` have not been set. Only used if button is rendered as `<a>`.
     */
    get rel() {
        var _a;
        const parts = ((_a = this._rel) === null || _a === void 0 ? void 0 : _a.split(' ')) || [];
        if (this.target === '_blank') {
            if (!parts.some((part) => ['noreferrer', 'noopener'].includes(part))) {
                parts.push('noopener');
            }
        }
        return parts.join(' ');
    }
    connectedCallback() {
        super.connectedCallback();
        if (this.emitConnectedCallback) {
            emit(this, 'connected-callback');
        }
    }
    willUpdate(changedProperties) {
        super.willUpdate(changedProperties);
        if (changedProperties.has('campaignsObj')) {
            if (this.campaignsObj && this.href) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const updatedHref = updateDynamicLinks(this.href || '', this.campaignsObj, this.endpoint);
                this.href = updatedHref.length ? updatedHref : this.href;
            }
        }
    }
    emitEvent(event) {
        if (this.isModalToggle) {
            modalToggleEvent(this, event);
        }
        else {
            emit(this, `${this._name}.click`, {
                detail: { modalToggle: this.isModalToggle, modalTarget: this.modalTarget, event },
            });
        }
    }
    // TODO: rel should be the same as cipublic-link, find way to abstract this
    render() {
        const { download, href, rel, target, type, disabled } = this;
        return href
            ? html `<a download="${ifDefined(download)}" href="${href}" rel="${ifDefined(rel)}" target="${ifDefined(target)}" class="${classMap({ disabled })}" aria-label="${ifDefined(this.ariaLabeling)}"><slot name="prefix"></slot><slot></slot><slot name="suffix"></slot></a>`
            : html `<button ?disabled="${disabled}" type="${ifDefined(type)}" aria-label="${ifDefined(this.ariaLabeling)}" @click="${(event) => {
                this.emitEvent(event);
                if (this.type === 'submit') {
                    submitForm(this.closest('cipublic-form'));
                }
            }}"><slot name="prefix"></slot><slot></slot><slot name="suffix"></slot></button>`;
    }
}
__decorate([
    property({ type: Boolean, reflect: true })
], Button.prototype, "disabled", void 0);
__decorate([
    property({ reflect: true })
], Button.prototype, "download", void 0);
__decorate([
    property({ reflect: true })
], Button.prototype, "href", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], Button.prototype, "iconOnly", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], Button.prototype, "isModalToggle", void 0);
__decorate([
    property({ attribute: 'data-modal-target' })
], Button.prototype, "modalTarget", void 0);
__decorate([
    property({ reflect: true })
], Button.prototype, "rel", null);
__decorate([
    property({ reflect: true })
], Button.prototype, "size", void 0);
__decorate([
    property({ reflect: true })
], Button.prototype, "target", void 0);
__decorate([
    property()
], Button.prototype, "type", void 0);
__decorate([
    property({ reflect: true })
], Button.prototype, "variant", void 0);
__decorate([
    property({ reflect: true, attribute: 'arialabeling' })
], Button.prototype, "ariaLabeling", void 0);
__decorate([
    state()
], Button.prototype, "campaignsObj", void 0);
__decorate([
    state()
], Button.prototype, "endpoint", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], Button.prototype, "emitConnectedCallback", void 0);
//# sourceMappingURL=button.js.map